// ============================================================
// ELC Shift — Uploads / Trading Pattern module (v97.0)
//
// The "trading pattern" is the AI rota generator's per-store config:
// shift templates, max hours, peak/quiet days, weekend rules, regional
// rules. This module exposes:
//
//   Uploads.trading.load()             — read trading pattern from
//                                        localStorage with DEFAULT_TRADING
//                                        fallback.
//   Uploads.trading.buildContext(tp)   — build the AI prompt context
//                                        string for rota generation,
//                                        including resolved custom rules
//                                        for the target store.
//
// Two related stubs deleted in v97.0 (had zero external callers):
//   • saveTradingPattern (1-line stub → Uploads.rules.saveAllRules())
//   • renderTradingPage  (1-line stub → Uploads.rules.renderRulesForScope())
//
// v97.0 follow-on fix: `buildContext` previously had a `typeof
// loadAllCustomRules === 'function'` guard that silently took the
// `[]` fallback path after v96.0 moved loadAllCustomRules into the
// rules module. Result: AI-generated rotas never included custom rules
// since v96.0 shipped. Now imported explicitly, so the guard isn't
// needed.
//
// Imports:
//   - DEFAULT_TRADING       (from ./rules.js — canonical default config)
//   - loadAllCustomRules    (from ./rules.js — for the rule cascade)
// ============================================================

import { DEFAULT_TRADING, loadAllCustomRules } from './rules.js';
import { weekendFor, peakDaysFor } from '../shared/rota-country.js';   // v133.94

// v133.94: is this pattern's store in Qatar? null when the store or the lookup is not known
function isQatarStore(tp) {
  try { return (tp && tp._storeName && typeof getStoreCountry === 'function') ? getStoreCountry(tp._storeName) === 'ELC - Qatar' : null; } catch (e) { return null; }
}
export function load() {
  try { return Object.assign({}, DEFAULT_TRADING, JSON.parse(localStorage.getItem('elc_trading_pattern') || '{}')); }
  catch { return Object.assign({}, DEFAULT_TRADING); }
}

export function buildContext(tp) {
  tp = tp || load();
  const evening = 100 - (tp.morningSplit || 50);
  const templates = (tp.shiftTemplates || DEFAULT_TRADING.shiftTemplates);
  const templateStr = templates.map(t => `  ${t.label} (${t.type}): ${t.start}–${t.finish}`).join('\n');

  const lines = [
    '═══ SHIFT TEMPLATES ═══',
    'Use ONLY these shift times (each is exactly 9h on site):',
    templateStr,
    '',
    '═══ SCHEDULING RULES ═══',
    `Max hours per person per week: ${tp.maxHours}h`,
    `Lunch break: ${tp.lunchDuration} minutes, staggered every ${tp.lunchStagger} minutes — never two people with the same lunch time on the same day.`,
    `Minimum staff on floor at any hour: ${tp.minFloor} person(s). Flag a warning if any hour drops below this.`,
    `Close cover: at least ${tp.closeCoverCount} person(s) must work until close every single day — no exceptions.`,
    `Days off permitted on: ${(tp.allowedDaysOff||[]).join(', ') || 'any day'} ONLY. Never place a day off outside these days.`,
    '',
    '═══ TRADING PATTERN ═══',
    `Sales split: ${tp.morningSplit}% morning (09:00–17:00) vs ${evening}% evening (17:00–close).`,
    evening >= 50
      ? `Evening dominates. Prioritise mid and late shifts over early shifts. Ensure strong cover after 17:00.`
      : `Morning dominates. Balance early and late shifts. Ensure strong opening cover.`,
    // v133.94: the store's own country when it is known, else both
    (isQatarStore(tp) == null
      ? `Peak days (maximise late shifts + headcount): UAE ${peakDaysFor(false, tp).join(', ')}; Qatar ${peakDaysFor(true, tp).join(', ')}.`
      : `Peak days (maximise late shifts + headcount): ${peakDaysFor(isQatarStore(tp), tp).join(', ')}.`),
    `Preferred days off (quietest trading): ${(tp.quietDays||[]).join(', ') || 'none set'}.`,
    '',
    '═══ REGIONAL RULES ═══',
    `UAE stores — never place D/O on: ${weekendFor(false, tp).join(', ')}.`,
    `Qatar stores — never place D/O on: ${weekendFor(true, tp).join(', ')}.`,
    tp.fridayQatar ? `Qatar Friday: ${tp.fridayQatar}` : '',
    tp.otherNotes  ? `Additional: ${tp.otherNotes}` : '',
  ];

  // Add all active custom rules relevant to this store
  const storeName = tp._storeName || '';
  const storeCity = storeName.split(' - ')[0] || '';
  const storeCountry = storeName.startsWith('Doha') ? 'ELC - Qatar' : 'ELC - UAE';
  const allCustomRules = loadAllCustomRules();
  const relevantRules = allCustomRules.filter(r => {
    if (r.active === false) return false;
    if (!r.scope || r.scope === 'business') return true;
    if (r.scope === 'country') return r.scopeValue === storeCountry;
    if (r.scope === 'city')    return storeName.startsWith(r.scopeValue);
    if (r.scope === 'store')   return r.scopeValue === storeName;
    return false;
  });

  if (relevantRules.length) {
    lines.push('', '═══ STANDALONE RULES ═══');
    lines.push('The following rules MUST be followed exactly as written:');
    relevantRules.forEach((r, i) => {
      lines.push(`${i+1}. ${r.name ? '['+r.name+'] ' : ''}${r.text}`);
    });
  }

  lines.push(
    '',
    '═══ CLOSE COVER ═══',
    `Every working day must have at least ${tp.closeCoverCount} person(s) scheduled until close.`,
    'Weekday close = standard store closing time. Weekend close = midnight (24:00).',
    'Each day must include a mix of early AND late shifts — never all-early or all-late.',
  );

  return lines.filter(Boolean).join('\n');
}
